/**
 * Build the guardrail context from resolved DSL + binding data.
 *
 * (The DSL→TypeScript codegen context builder, `buildContractContext`, was
 * removed alongside the runtime `generate()` codegen path.)
 */

export interface GuardrailCheckEntry {
  guardrail_id: string;
  description: string;
  severity: string;
  action: string;
  matcher_type: string;
  pattern: string;
  message: string;
  file_glob?: string;
  exclude_glob?: string;
}

export interface GuardrailContext {
  commandChecks: GuardrailCheckEntry[];
  fileChecks: GuardrailCheckEntry[];
  contentChecks: GuardrailCheckEntry[];
  allChecks: GuardrailCheckEntry[];
  hasGuardrails: boolean;
}

/**
 * Build guardrail context from DSL guardrails, binding guardrail_impl, and active policy.
 * Mirrors the agent-contracts resolveChecks pattern.
 */
export function buildGuardrailContext(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dsl: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  binding: any,
  policyName?: string,
): GuardrailContext {
  const empty: GuardrailContext = {
    commandChecks: [],
    fileChecks: [],
    contentChecks: [],
    allChecks: [],
    hasGuardrails: false,
  };

  if (!binding?.guardrail_impl) return empty;

  const guardrails = dsl.guardrails ?? {};
  const policies = dsl.guardrail_policies ?? {};
  const policy = policyName ? policies[policyName] : undefined;

  const allChecks: GuardrailCheckEntry[] = [];

  for (const [guardrailId, impl] of Object.entries(
    binding.guardrail_impl as Record<string, { checks: Array<Record<string, unknown>> }>,
  )) {
    const guardrail = guardrails[guardrailId] as Record<string, unknown> | undefined;
    const description = (guardrail?.description as string) ?? guardrailId;

    const policyRule = policy?.rules?.find(
      (r: Record<string, unknown>) => r.guardrail === guardrailId,
    );
    const severity = (policyRule?.severity as string) ?? "warning";
    const action = (policyRule?.action as string) ?? "warn";

    for (const check of impl.checks) {
      const matcher = check.matcher as Record<string, unknown> | undefined;
      if (!matcher) continue;

      allChecks.push({
        guardrail_id: guardrailId,
        description,
        severity,
        action,
        matcher_type: matcher.type as string,
        pattern: matcher.pattern as string,
        message: (check.message as string) ?? description,
        file_glob: matcher.file_glob as string | undefined,
        exclude_glob: matcher.exclude_glob as string | undefined,
      });
    }
  }

  return {
    commandChecks: allChecks.filter((c) => c.matcher_type === "command_regex"),
    fileChecks: allChecks.filter((c) => c.matcher_type === "file_glob"),
    contentChecks: allChecks.filter((c) => c.matcher_type === "content_regex"),
    allChecks,
    hasGuardrails: allChecks.length > 0,
  };
}
